import {
  Box,
  CircularProgress,
  Text,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react"
import axios from "axios"
import React from "react"
import { api } from "config"
import {
  PayPalScriptProvider,
  PayPalButtons,
} from "@paypal/react-paypal-js"
import { Group } from "types/apiTypes"

type PayPalProps = {
  selectedMonthIds: number[]
  voucherDetails?: { code?: string }
  paymentPersonId: number | null
  group?: Group
  isMulti?: boolean
}

const PayPal = ({
  selectedMonthIds,
  voucherDetails,
  paymentPersonId,
}: PayPalProps) => {
  const [clientId, setClientId] = React.useState<string | null>(null)
  const [tokenError, setTokenError] = React.useState(false)
  const toast = useToast()

  React.useEffect(() => {
    axios
      .get(`${api}/payments/paypal-client-token`, { withCredentials: true })
      .then((r) => {
        setClientId(r.data.clientId)
      })
      .catch(() => setTokenError(true))
  }, [])

  const showError = (msg: string) =>
    toast({
      title: "Payment Failed",
      description: msg || "Please check your details and try again.",
      status: "error",
      duration: 6000,
      isClosable: true,
    })

  return (
    <Box my={4}>
      <Text textAlign="center" fontSize="2xl" fontWeight="bold" mb={6}>
        Pay with PayPal or Card
      </Text>

      {tokenError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Unable to initialize PayPal. Please refresh and try again.
          </AlertDescription>
        </Alert>
      ) : !clientId ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress isIndeterminate color="blue.500" />
        </Box>
      ) : (
        <PayPalScriptProvider
          options={{
            clientId,
            intent: "capture",
            currency: "EUR",
            enableFunding: "card",
          }}
        >
          <VStack spacing={4} align="stretch">
            <PayPalButtons
              style={{ layout: "vertical", label: "pay" }}
              createOrder={async () => {
                const r = await axios.post(
                  `${api}/payments/create-paypal-order`,
                  {
                    monthIds: selectedMonthIds,
                    coupon: voucherDetails?.code,
                    paymentPersonId,
                  },
                  { withCredentials: true },
                )
                return r.data.orderID as string
              }}
              onApprove={async (data) => {
                try {
                  await axios.post(
                    `${api}/payments/capture-paypal-order-api`,
                    { orderID: data.orderID },
                    { withCredentials: true },
                  )
                  window.location.href = "/admin/payment-confirmation?paypal=success"
                } catch (err: any) {
                  showError(err?.message ?? "Failed to record payment")
                }
              }}
              onError={(err: any) => {
                showError(
                  String(err?.message ?? err) ||
                    "Payment failed. Please try again.",
                )
              }}
            />
          </VStack>
        </PayPalScriptProvider>
      )}
    </Box>
  )
}

export default PayPal
